import { BillingApi } from "@/lib/api/billing-api";
import Toast from "@/lib/components/Toast";
import { useCurrentTeamMembership } from "@/lib/hooks/team-hooks";
import { useQuickAnimationCreditsQuery } from "@/lib/queries/animated-image-queries";
import { queryClient } from "@/lib/queries/query-client";
import { teamQueryKeys, useTeamQuery } from "@/lib/queries/team-queries";
import { filterTimelineItemsToAnimate } from "@/lib/stores/editor-store/editor-state";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { CouponDto } from "@repo/api/coupon";
import { TeamDto } from "@repo/api/team";
import { TeamPermissions } from "@repo/api/team-roles";
import {
  CREDITS_FIRST_ANIMATION,
  getMinCredits,
} from "@repo/api/utils/billing";
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";

export function useCanOpenCreditsDialog(
  isOpen: boolean,
  setIsOpen: (isOpen: boolean) => void,
  teamExternalId: string,
) {
  const [canOpenCreditsDialog, setCanOpenCreditsDialog] = useState(false);
  const { data: team } = useTeamQuery(teamExternalId);
  const teamMembership = useCurrentTeamMembership();

  useEffect(() => {
    if (!team || !teamMembership) {
      return;
    }

    const canOpenCreditsDialog =
      teamMembership.permissions.includes(TeamPermissions.BILLING_EDIT) &&
      team.billingInfo.showCredits;

    if (!canOpenCreditsDialog && isOpen) {
      Toast.info(
        "Ask your admin to buy credits for your team, you don't have permission to do it.",
      );
      setIsOpen(false);
    }

    setCanOpenCreditsDialog(canOpenCreditsDialog);
  }, [team, teamMembership, isOpen, setIsOpen]);

  return canOpenCreditsDialog;
}

export function useSyncBillingAfterPayment(teamExternalId: string) {
  const [isSyncingBilling, setIsSyncingBilling] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    async function syncBilling() {
      const status = searchParams.get("payment_status");

      if (!status) {
        return;
      }

      setIsSyncingBilling(true);

      try {
        if (status === "success") {
          await BillingApi.syncBilling(teamExternalId);
          await queryClient.invalidateQueries({
            queryKey: teamQueryKeys.getTeam(teamExternalId),
          });
          Toast.info("Credits purchased successfully");
        } else if (status === "cancel") {
          Toast.error("Credits purchase cancelled");
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsSyncingBilling(false);
        setSearchParams((prev) => {
          prev.delete("payment_status");
          return prev;
        });
      }
    }

    syncBilling();
  }, [searchParams, teamExternalId, setSearchParams]);

  return isSyncingBilling;
}

export function useMissingCredits(teamExternalId: string) {
  const { data: team } = useTeamQuery(teamExternalId);
  const { credits, numSelected } = useCreditsForAnimation();

  if (!team || !team.billingInfo.showCredits || credits === undefined) {
    return {};
  }

  let missingCredits = Math.max(credits - team.billingInfo.creditBalance, 0);

  if (missingCredits > 0 && missingCredits < getMinCredits()) {
    missingCredits = getMinCredits();
  }

  const numAnimatableImages = Math.floor(
    team.billingInfo.creditBalance / CREDITS_FIRST_ANIMATION,
  );

  return {
    missingCredits,
    numAnimatableImages,
    numSelected,
  };
}

export function useCreditsForAnimation() {
  const { teamExternalId } = useParams();
  const timelineItems = useEditorStore(
    (state) => state.video!.compositionJson.mainSceneTimeline,
  );
  const formItems = useEditorStore((state) => state.animationForm.items);

  const { sourceImageExternalIds, numSelected } = useMemo(() => {
    const itemsToAnimate = filterTimelineItemsToAnimate(
      timelineItems ?? [],
      formItems,
    );

    const ids = Object.keys(itemsToAnimate).map((itemId) => {
      const externalId = timelineItems.find(
        (item) => item.externalId === itemId,
      )?.startSourceImageExternalId;

      if (!externalId) {
        throw new Error(
          `Start source image external id not found for item ${itemId}`,
        );
      }

      return externalId;
    });

    return {
      sourceImageExternalIds: ids,
      numSelected: Object.keys(itemsToAnimate).length,
    };
  }, [formItems, timelineItems]);

  // Sort the IDs so that semantically equivalent selections produce the same
  // query key and can share a cached result.
  const sortedSourceImageExternalIds = useMemo(
    () => [...sourceImageExternalIds].sort(),
    [sourceImageExternalIds],
  );

  const { data, isPending } = useQuickAnimationCreditsQuery({
    teamExternalId: teamExternalId!,
    sourceImageExternalIds: sortedSourceImageExternalIds,
  });

  return {
    credits: numSelected === 0 ? 0 : data?.credits,
    numSelected,
    isPending: numSelected > 0 && isPending,
  };
}

export function useCoupon(team: TeamDto) {
  const autoApplyCoupon = team.billingInfo?.autoApplyCoupon ?? null;

  const [currentCoupon, setCurrentCoupon] = useState<CouponDto | null>(
    autoApplyCoupon,
  );

  useEffect(() => {
    if (!currentCoupon) {
      return;
    }

    const interval = setInterval(() => {
      const now = new Date();
      const expiresAt = currentCoupon?.expiresAt
        ? new Date(currentCoupon.expiresAt)
        : null;

      if (expiresAt && now > expiresAt) {
        setCurrentCoupon(null);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [currentCoupon]);

  return {
    autoApplyCoupon,
    currentCoupon,
    setCurrentCoupon,
  };
}
